import { defineQueries, type Query } from "@rocicorp/zero";

export const WORKFLOW_RUNS_LIMIT = 250;

import * as z from "zod";
import {
  FEDERATED_API_KEY_NAME_PREFIX,
  USER_API_KEY_NAME_PREFIX,
} from "./apiKeyPrefix";
import {
  type Context,
  principalMatch,
  canReadApiKey,
  canReadAuditLog,
  canReadAuditLogDestination,
  canReadBranch,
  canManageConnector,
  canReadConnector,
  canUseAIProvider,
  canReadDeletedWorkflow,
  canReviewGrantRequest,
  canReadDuplicateWorkflowPair,
  canReadDuplicateWorkflowReview,
  canReadEgressRule,
  canReadNetwork,
  canReadOidcProvider,
  canReadRouteFavorite,
  canReadRun,
  canReadServiceAccount,
  canReadSkill,
  canReadSpace,
  canReadSpaceAsMemberOrDiscover,
  canReadSsoProvider,
  canReadWorkflow,
  canReadWorkflowFavorite,
  canReadWorkflowView,
  canReadPersonalChat,
  canReadPersonalChats,
  personalWorkAllowed,
  isHumanManagedServiceAccountKey,
} from "./authorization";
import {
  type AuditLog,
  type BranchContributor,
  type ChatEntry,
  type CommitRoute,
  type ConnectedAppUsage,
  type ConnectorUsage,
  type RoleGrant,
  type Schema,
  type Space,
  type Workflow,
  type WorkflowBranch,
  type WorkflowCommit,
  zql,
} from "./generatedDrizzleSchema";
import { escapeLikePattern } from "./likePattern";
import type { NormalizedName } from "./normalizeName";
import {
  commitStepUsages,
  listedConnectorUsageTypes,
} from "./liveConnectorUsage";
import { paginateQuery } from "./paginateQuery";
import { ALLOW_ALL_ROLE_IDS } from "./rbac/roles";
import { defineTenantQuery } from "./defineTenantQuery";
import { ssoRouteDependenciesQuery } from "./ssoRouteDependencies";
import { ownConnectorGrants } from "./connectors/ownConnectorGrants";
import { SPACE_MODES } from "./spaceMode";

const chatEntryDetails = (e: Query<"chatEntries", Schema, ChatEntry>) =>
  e
    .related("userMessage", (um) =>
      um.related("user").related("serviceAccount")
    )
    .related("assistantMessage")
    .related("toolCall")
    .related("assistantReasoning")
    .related("systemMessage")
    .related("userAction", (ua) => ua.related("user"))
    .related("compaction")
    .related("attachments");

const fullChatEntries = (e: Query<"chatEntries", Schema, ChatEntry>) =>
  chatEntryDetails(e).orderBy("createdAt", "asc").orderBy("id", "asc");

const chatHistoryCursor = z.object({ createdAt: z.number(), id: z.string() });
type ChatHistoryCursor = z.infer<typeof chatHistoryCursor>;
export type ChatHistoryPage = {
  limit?: number;
  after?: ChatHistoryCursor;
  from?: ChatHistoryCursor;
};

export const isUnboundedChatHistoryPage = (page: ChatHistoryPage) =>
  page.limit === undefined && !page.after && !page.from;

// Reverse newest-first results before displaying the transcript chronologically.
const newestChatEntries = (
  e: Query<"chatEntries", Schema, ChatEntry>,
  page: ChatHistoryPage
) => {
  let entries = chatEntryDetails(e)
    .orderBy("createdAt", "desc")
    .orderBy("id", "desc");
  if (page.from) {
    const from = page.from;
    // The explicit timestamp bound lets SQLite use a range scan before breaking ties by ID.
    entries = entries
      .where("createdAt", ">=", from.createdAt)
      .where(({ or, and, cmp }) =>
        or(
          cmp("createdAt", ">", from.createdAt),
          and(cmp("createdAt", from.createdAt), cmp("id", ">=", from.id))
        )
      );
  }
  return paginateQuery(entries, page);
};

const latestUserMessageEntry = (e: Query<"chatEntries", Schema, ChatEntry>) =>
  e.where("entryType", "UserMessage").orderBy("createdAt", "desc").limit(1);

const readablePersonalChats = (ctx: Context, userId?: string) => {
  const chats =
    userId === undefined
      ? zql.personalChats
      : zql.personalChats.where("userId", userId);
  return userId === ctx.userId ? chats : chats.where(canReadPersonalChats(ctx));
};

const withPersonalSpaceOwner = (
  s: Query<"spaces", Schema, Space | undefined>
) => s.related("roleGrantsAsScope", (rg) => rg.related("user").limit(1));

const recentContributors =
  (since: number) =>
  (c: Query<"branchContributors", Schema, BranchContributor>) =>
    c
      .where("lastMessagedAt", ">=", since)
      .related("user")
      .orderBy("lastMessagedAt", "desc")
      .limit(25);

export function roundedHoursAgo(hours: number): number {
  const oneHour = 60 * 60 * 1000;
  return Math.floor((Date.now() - hours * oneHour) / oneHour) * oneHour;
}

// Day-grained windows for the heavy monitoring queries: every distinct `since`
// is a brand-new query the view-syncer must hydrate from scratch, so rounding
// to the day instead of the hour makes that 24× rarer.
export function roundedDaysAgo(days: number): number {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor((Date.now() - days * oneDay) / oneDay) * oneDay;
}

// A duplicate-pair side with everything its review card renders: space badge,
// live-branch badge state, and connector avatars.
const monitorPairWorkflow = (
  w: Query<"workflows", Schema, Workflow | undefined>
) =>
  w.related("space", withPersonalSpaceOwner).related("branches", (b) =>
    b
      .where("name", "main")
      .where("headCommitId", "IS NOT", null)
      .related(
        "headCommit",
        (c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>) =>
          c
            .related("appUsages", (u) =>
              commitStepUsages(u).related("connectedApp", (a) =>
                a.related("oauthConnection")
              )
            )
            .one()
      )
      .one()
  );

const featured = (r: Query<"commitRoutes", Schema, CommitRoute>) =>
  r.where("routeType", "IS NOT", null);

const workflowBranches =
  (userId: string, branchId?: string) =>
  (b: Query<"workflowBranches", Schema, WorkflowBranch>) => {
    const base = branchId ? b.where("id", branchId) : b;
    return base
      .where(({ or, cmp }) => or(cmp("name", "main"), cmp("userId", userId)))
      .related("user")
      .related("headCommitChat")
      .related("headCommit", (c) =>
        c.related("routes", featured).related("schedules").one()
      )
      .related("headConnectorUsages")
      .orderBy("createdAt", "asc");
  };

const workflowListArgs = z.object({
  activitySince: z.number().optional(),
});

const recentWorkflowDetails = <R extends Workflow | undefined>(
  w: Query<"workflows", Schema, R>,
  ctx: Context,
  args: z.infer<typeof workflowListArgs>
) =>
  w
    .related("space", withPersonalSpaceOwner)
    .related("createdBy")
    .related("favorites", (f) => f.where("userId", ctx.userId).limit(1))
    .related(
      "contributors",
      recentContributors(args.activitySince ?? Number.MAX_SAFE_INTEGER)
    )
    .related("branches", workflowBranches(ctx.userId));

const workflowBranchSummaries = (
  b: Query<"workflowBranches", Schema, WorkflowBranch>
) =>
  b
    .related("user")
    .related("contributors", (c) => c.related("user"))
    .related("commits", (c) => c.where("message", "IS NOT", null))
    .related("headCommitChat")
    .related("spaceBranchMembers", (m) => m.where("deletedAt", "IS NOT", null))
    .orderBy("createdAt", "asc");

const roleGrantsWithMembers = (g: Query<"roleGrants", Schema, RoleGrant>) =>
  g
    .related("customRole", (cr) => cr.related("rolePermissions"))
    .related("userGroup", (ug) =>
      ug.related("roleGrantsAsScope", (mg) => mg.related("user")).one()
    )
    .related("user");

// Grant consumers use principal IDs and load principal details separately.
const leanScopeGrants = (g: Query<"roleGrants", Schema, RoleGrant>) =>
  g.related("customRole", (cr) => cr.related("rolePermissions"));

const connectorDetails = (connectedAppId: string, ctx: Context) =>
  zql.connectedApps
    .where("id", connectedAppId)
    .where(canReadConnector(ctx))
    .related("oauthConnection")
    .related("visitorGrants", (g) =>
      g
        .where(({ or, and, cmp, exists }) => {
          const canManage = exists(
            "grantedConnector",
            (c) => c.where(canManageConnector(ctx)),
            { flip: false }
          );
          return ctx.principal?.kind === "serviceAccount"
            ? canManage
            : or(
                canManage,
                and(cmp("subjectKind", "user"), cmp("subjectId", ctx.userId))
              );
        })
        .orderBy("createdAt", "asc")
    )
    .related("roleGrantsAsScope", leanScopeGrants)
    .one();

const nameKeyset = {
  limit: z.number().optional(),
  after: z.object({ name: z.string(), id: z.string() }).optional(),
};

const connectorIndexArgs = z
  .object({
    sort: z.enum(["name", "created_at"]).optional(),
    order: z.enum(["asc", "desc"]).optional(),
    createdAfter: z.number().optional(),
    limit: z.number().optional(),
    after: z
      .object({ name: z.string(), createdAt: z.number(), id: z.string() })
      .optional(),
  })
  .optional();

const connectorIndex = (
  ctx: Context,
  args: z.infer<typeof connectorIndexArgs>
) => {
  const order = args?.order ?? "asc";
  let query = zql.connectedApps
    .where("fresh", false)
    .where(canReadConnector(ctx))
    .orderBy(args?.sort === "created_at" ? "createdAt" : "name", order)
    .orderBy("id", order)
    .related("oauthConnection")
    .related("createdBy")
    .related("roleGrantsAsScope", leanScopeGrants);
  if (args?.createdAfter !== undefined)
    query = query.where("createdAt", ">", args.createdAfter);
  return paginateQuery(query, args);
};

export const queries = defineQueries({
  allUsers: defineTenantQuery(
    z
      .object({
        q: z.string().optional(),
        limit: z.number().optional(),
        after: z.object({ createdAt: z.number(), id: z.string() }).optional(),
      })
      .optional(),
    ({ args }) => {
      let query = zql.user.orderBy("createdAt", "asc").orderBy("id", "asc");
      const q = args?.q?.trim();
      if (q) {
        const pattern = `%${escapeLikePattern(q)}%`;
        query = query.where(({ or, cmp }) =>
          or(
            cmp("name", "ILIKE", pattern as NormalizedName),
            cmp("email", "ILIKE", pattern)
          )
        );
      }
      return paginateQuery(query, args);
    }
  ),

  spaceMembers: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      roleGrantsWithMembers(
        zql.roleGrants
          .where("scopeId", args.spaceId)
          .whereExists("scopeAsSpace", (s) => s.where(canReadSpace(ctx)))
      )
  ),

  userBranchPresence: defineTenantQuery(
    z.object({ since: z.number().optional() }).default({}),
    ({ args, ctx }) =>
      zql.userBranchPresence
        .where("lastSeenAt", ">", args.since ?? 0)
        .whereExists("branch", (b) => b.where(canReadBranch(ctx)))
        .related("user")
  ),

  currentUser: defineTenantQuery(({ ctx }) =>
    zql.user.where("id", ctx.userId).one()
  ),

  currentTenant: defineTenantQuery(() => zql.tenants.one()),

  tenantSealingKeys: defineTenantQuery(() =>
    zql.tenantKeys.where("retiredAt", "IS", null)
  ),

  personalSpace: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "personal")
      .whereExists("roleGrantsAsScope", (rg) => rg.where("userId", ctx.userId))
      .one()
  ),

  personalSpaceOwners: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "personal")
      .where(canReadSpace(ctx))
      .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
  ),

  spaces: defineTenantQuery(
    z.object({
      settingsView: z.boolean(),
    }),
    ({ ctx, args: { settingsView } }) =>
      zql.spaces
        .where(
          settingsView ? canReadSpace(ctx) : canReadSpaceAsMemberOrDiscover(ctx)
        )
        .related("roleGrantsAsScope", leanScopeGrants)
        .orderBy("name", "asc")
  ),

  // Only relation is the current user's own grants on each space, so the UI
  // can tell member from discoverable-joiner without a second query.
  spacesLite: defineTenantQuery(
    z.object({
      includeAdminSpaces: z.boolean().optional(),
      modes: z.array(z.enum(SPACE_MODES)).default([...SPACE_MODES]),
    }),
    ({ ctx, args: { includeAdminSpaces, modes } }) =>
      zql.spaces
        .where("mode", "IN", modes)
        .where(
          includeAdminSpaces
            ? canReadSpace(ctx)
            : canReadSpaceAsMemberOrDiscover(ctx)
        )
        .related("roleGrantsAsScope", (rg) =>
          rg.where(principalMatch(ctx.userId))
        )
        .orderBy("name", "asc")
  ),

  // Spaces with the grant detail needed to render the Settings access-map
  // visualization. Availability only — no user or other display relations. When
  // `spaceIds` is given the result is scoped to those spaces; otherwise it
  // covers every readable space. `canReadSpace` stays the authorization guard
  // regardless of the ids passed.
  spaceAvailabilityGrants: defineTenantQuery(
    z.object({ spaceIds: z.array(z.string()).optional() }).default({}),
    ({ ctx, args: { spaceIds } }) =>
      (spaceIds === undefined
        ? zql.spaces.where(canReadSpace(ctx))
        : zql.spaces.where((eb) =>
            eb.and(canReadSpace(ctx)(eb), eb.cmp("id", "IN", spaceIds))
          )
      ).related("roleGrantsAsScope", (rg) =>
        rg.related("customRole", (cr) => cr.related("rolePermissions"))
      )
  ),

  // Avoid relations to keep snappy
  skillsLite: defineTenantQuery(
    z
      .object({
        id: z.string().optional(),
        name: z.string().optional(),
        published: z.boolean().optional(),
      })
      .optional(),
    ({ args, ctx }) => {
      let query = zql.skills.where(canReadSkill(ctx)).orderBy("name", "asc");
      if (args?.id) query = query.where("id", args.id);
      if (args?.name) query = query.where("name", args.name);
      if (args?.published)
        query = query.where("enabled", true).where("fresh", false);
      return query;
    }
  ),

  // Avoid most relations
  connectedAppsLite: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("createdBy")
  ),

  monitorAiConnectors: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
  ),

  userPreferences: defineTenantQuery(({ ctx }) =>
    zql.userPreferences.where("userId", ctx.userId).one()
  ),

  myConnectorGrants: defineTenantQuery(
    z.object({ email: z.string().optional() }).optional(),
    ({ ctx, args }) =>
      ownConnectorGrants(ctx, args?.email)
        .related("grantedConnector", (app) => app.where(canReadConnector(ctx)))
        .orderBy("createdAt", "asc")
  ),

  mySharedConnectorAccounts: defineTenantQuery(({ ctx }) =>
    zql.oauthConnections
      .where("authorizedByUserId", ctx.userId)
      .where("connectedAppId", "IS", null)
      .whereExists("connectedApps", (app) => app.where(canReadConnector(ctx)))
      .related("connectedApps", (app) => app.where(canReadConnector(ctx)))
      .orderBy("createdAt", "asc")
  ),

  tenantCustomDomains: defineTenantQuery(() =>
    zql.tenantCustomDomains.orderBy("createdAt", "asc")
  ),

  connectorsIndex: defineTenantQuery(connectorIndexArgs, ({ args, ctx }) =>
    connectorIndex(ctx, args)
  ),

  connectorCatalog: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
  ),

  connectorsWithUsages: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("createdBy")
      .related("roleGrantsAsScope", leanScopeGrants)
      .related("usages", (u) =>
        u
          .where("type", "!=", "commit_step")
          .where("type", "!=", "workflow_slug")
      )
  ),

  connectorListUsages: defineTenantQuery(({ ctx }) => {
    const listed = (
      u: Query<"connectedAppUsages", Schema, ConnectedAppUsage>
    ) => u.where("type", "IN", [...listedConnectorUsageTypes]);
    // Starting from the usages checks access only on connectors that have some.
    return zql.connectedApps
      .where("fresh", false)
      .whereExists("usages", listed, { flip: true })
      .where(canReadConnector(ctx))
      .related("usages", listed);
  }),

  // Pending share requests visible to the current user — their own, plus every
  // request they can review. Powers the pinned group on the connectors list.
  pendingGrantRequests: defineTenantQuery(({ ctx }) =>
    zql.grantRequests
      .where(canReviewGrantRequest(ctx))
      .orderBy("createdAt", "desc")
      .related("requestedByUser")
      .related("requestedGrants")
      .related("scopeAsConnector", (c) => c.related("oauthConnection"))
  ),

  // The pending request (if any) for one scope — the connector modal's banner
  // and the requester's pending state.
  grantRequestForScope: defineTenantQuery(
    z.object({ scopeId: z.string() }),
    ({ args, ctx }) =>
      zql.grantRequests
        .where("scopeId", args.scopeId)
        .where(canReviewGrantRequest(ctx))
        .orderBy("createdAt", "desc")
        .related("requestedByUser")
        .related("requestedGrants")
        .one()
  ),

  // whereExists keeps the badge count aligned with the connector-only Requests
  // list, which drops non-connector share requests.
  reviewableGrantRequests: defineTenantQuery(({ ctx }) =>
    zql.grantRequests
      .where(canReviewGrantRequest(ctx))
      .where((eb) => eb.cmp("requestedByUserId", "!=", ctx.userId))
      .whereExists("scopeAsConnector")
  ),

  skillsIndex: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where("fresh", false)
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("gitInstallation", (installation) =>
        installation.related("source")
      )
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  skillsManagementIndex: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("gitInstallation", (installation) =>
        installation.related("source")
      )
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  // Lean projections for the access map: the same rows as the *Index queries but
  // only the grant detail the graph reads, dropping the list-page relations
  // (usages, createdBy, principal grants) it never touches. oauthConnection
  // stays because ConnectorLogo's prop type requires it.
  accessGraphConnectedApps: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("roleGrantsAsScope", leanScopeGrants)
      // Usages resolve which connectors a space uses, for the drag-to-grant
      // auto-grant prompt; commit_step usages are huge and unneeded here.
      .related("usages", (u) => u.where("type", "!=", "commit_step"))
  ),

  accessGraphSkills: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where("fresh", false)
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  accessGraphGroups: defineTenantQuery(() =>
    zql.userGroups
      // Member `user` is kept for the invite-member modal's face piles (reached
      // from the access map); only the unused principal grants are dropped.
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .orderBy("name", "asc")
  ),

  loadSkill: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("createdBy")
        .related("gitInstallation", (installation) =>
          installation.related("source", (source) =>
            source.related("connectedApp", (app) =>
              app.where(canReadConnector(ctx))
            )
          )
        )
        .related("roleGrantsAsScope", leanScopeGrants)
        .one()
  ),

  skillByName: defineTenantQuery(
    z.object({ name: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("name", args.name)
        .where("fresh", false)
        .where(canReadSkill(ctx))
        .one()
  ),

  loadSkillUpdate: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("files", (files) => files.related("blob"))
        .related("gitInstallation", (installation) =>
          installation.related("candidateBlob")
        )
        .one()
  ),

  loadSkillFileIndex: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .orderBy("path", "asc")
  ),

  loadSkillFile: defineTenantQuery(
    z.object({ skillId: z.string(), path: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .where("path", args.path)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .related("blob")
        .one()
  ),

  loadSkillGitSource: defineTenantQuery(
    z.object({ sourceId: z.string() }),
    ({ args, ctx }) =>
      zql.skillGitSources
        .where("id", args.sourceId)
        .where("createdByUserId", ctx.userId)
        .related("connectedApp", (app) => app.where(canReadConnector(ctx)))
        .related("installations", (installation) =>
          installation.related("skill")
        )
        .one()
  ),

  skillGroupAccess: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  workflowAppSlugs: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "workflow_slug")
        .where("refId", args.workflowId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
  ),

  // Readable workflows already expose the connectors their steps use, so
  // callers match these against the connectors they can read.
  connectorUsagesBySpace: defineTenantQuery(
    z.object({ spaceIds: z.array(z.string()).optional() }).default({}),
    ({ ctx, args: { spaceIds } }) =>
      (spaceIds === undefined
        ? zql.spaces.where(canReadSpace(ctx))
        : zql.spaces.where("id", "IN", spaceIds).where(canReadSpace(ctx))
      ).related("workflows", (w) =>
        w.where("deletedAt", "IS", null).related("connectorUsages")
      )
  ),

  // Group removals only disconnect usages in shared spaces.
  sharedSpaceConnectorUsages: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "!=", "personal")
      .where(canReadSpace(ctx))
      .related("workflows", (w) =>
        w
          .where("deletedAt", "IS", null)
          .related("connectorUsages")
          .related("branches")
      )
  ),

  allWorkflows: defineTenantQuery(({ ctx }) =>
    zql.workflows.where(canReadWorkflow(ctx))
  ),

  spaceDeletionPreviewWorkflows: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("spaceId", args.spaceId)
        .where(canReadWorkflow(ctx))
        .orderBy("createdAt", "desc")
  ),

  // Workflows with an open Autofix/Autotune branch awaiting review, behind the
  // monitoring Autofix and Autotune card. The AI monitors rename the branch
  // shortly after creating it, so we can't filter branches by name; the card
  // matches each workflow's autofixBranchId/autoImproveBranchId pointer to its
  // branch. A push or reject clears the pointer, which drops the row.
  monitorAutofixAutotune: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .where(({ or, cmp }) =>
        or(
          cmp("autofixBranchId", "IS NOT", null),
          cmp("autoImproveBranchId", "IS NOT", null)
        )
      )
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) => b.related("headCommitChat"))
  ),

  monitorDuplicatePairs: defineTenantQuery(({ ctx }) =>
    zql.duplicateWorkflowPairs
      .where("verdict", "duplicate")
      .where(canReadDuplicateWorkflowPair(ctx))
      .orderBy("judgedAt", "desc")
      .related("workflowA", monitorPairWorkflow)
      .related("workflowB", monitorPairWorkflow)
  ),

  monitorDuplicateReviews: defineTenantQuery(({ ctx }) =>
    zql.duplicateWorkflowReviews.where(canReadDuplicateWorkflowReview(ctx))
  ),

  monitorDuplicateDetectionRun: defineTenantQuery(() =>
    zql.duplicateDetectionRuns.one()
  ),

  // Nesting stats under workflows runs the read-permission probe once per
  // workflow instead of once per (workflow, day) row.
  monitorWorkflowDailyStats: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.workflows
        .where(canReadWorkflow(ctx))
        .related("runDailyStats", (s) => s.where("day", ">=", args.since))
  ),

  connectorUsageDimensions: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectorUsageDimensions
        .where("connectorId", args.connectedAppId)
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
  ),

  connectedAppForWorkflow: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("backingWorkflowId", args.workflowId)
        .where("fresh", false)
        .where(canReadConnector(ctx))
        .orderBy("createdAt", "desc")
        .one()
  ),

  loadConnectorDetails: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) => connectorDetails(args.connectedAppId, ctx)
  ),

  // Steps in personal spaces drop out entirely rather than counting toward the
  // redacted remainder: with personal work disabled they're unreachable for
  // everyone, so a hidden-step count nobody can ever resolve is just noise.
  connectorUsageTotals: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("id", args.connectedAppId)
        .where(canReadConnector(ctx))
        .related("currentUses", (u) =>
          u.whereExists("workflow", (w) =>
            w
              .where("deletedAt", "IS", null)
              .whereExists("space", (s) => s.where(personalWorkAllowed))
          )
        )
        .related("usages", (u) =>
          u.where("type", "IN", [
            "personal_chat",
            "space_git_remote",
            "skill_git_source",
            "audit_log_destination",
            "ai_provider",
          ])
        )
        .one()
  ),

  loadConnectionClaim: defineTenantQuery(
    z.object({ claimId: z.string() }),
    ({ args, ctx }) =>
      zql.connectionClaims
        .where("id", args.claimId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
        .one()
  ),

  connectorUsageDetails: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) => {
      const usages = (u: Query<"connectorUsages", Schema, ConnectorUsage>) =>
        u.where("connectorId", args.connectedAppId);
      // Starting from the connector's usages scales with its workflows, not
      // the tenant's.
      return zql.workflows
        .whereExists("connectorUsages", usages, { flip: true })
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("connectorUsages", usages)
        .related("branches")
        .related("stepActivity");
    }
  ),

  // Personal-chat usages, filtered to chats the caller can read.
  loadConnectedAppPersonalChatUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("connectedAppId", args.connectedAppId)
        .where(canReadPersonalChat(ctx))
        .related("personalChat", (pc) => pc.related("user"))
  ),

  // Count-only source for personal chats a change would disconnect: bare usage
  // rows (no owner or title) for open chats owned by the given users, so the
  // caller can size chat impact it isn't allowed to see individually.
  connectorPersonalChatUsageForUsers: defineTenantQuery(
    z.object({ connectedAppId: z.string(), userIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
        .whereExists("personalChat", (pc) =>
          pc.where("userId", "IN", args.userIds).where("closedAt", "IS", null)
        )
  ),

  // Git-sync usages, filtered to spaces the caller can read.
  loadConnectedAppGitRemoteUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "space_git_remote")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("space", withPersonalSpaceOwner)
        .related("spaceGitRemote")
  ),

  // Audit-log-destination usages, filtered to destinations the caller can read.
  loadConnectedAppAuditUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "audit_log_destination")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("auditLogDestination", (d) =>
          d.where(canReadAuditLogDestination(ctx))
        )
        .related("auditLogDestination")
  ),

  loadConnectedAppAiProviderUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args }) =>
      zql.connectedAppUsages
        .where("type", "ai_provider")
        .where("connectedAppId", args.connectedAppId)
        .related("aiProvider")
  ),

  // Git skill sources backed by this connector. A source is only ever readable
  // by its creator, so everyone else's surface as a redacted count instead.
  loadConnectedAppSkillSourceUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.skillGitSources
        .where("connectedAppId", args.connectedAppId)
        .where("createdByUserId", ctx.userId)
  ),

  // Starting from the groups' grants checks access only on their connectors.
  groupConnectors: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedApps
        .whereExists(
          "roleGrantsAsScope",
          (g) => g.where("userGroupId", "IN", args.userGroupIds),
          { flip: true }
        )
        .where(canReadConnector(ctx))
        .related("roleGrantsAsScope", leanScopeGrants)
  ),

  // Git-sync usages of every connector granted to one of these groups,
  // restricted to readable spaces.
  groupConnectorGitRemoteUsage: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "space_git_remote")
        .whereExists("connectedApp", (a) =>
          a
            .where(canReadConnector(ctx))
            .whereExists("roleGrantsAsScope", (g) =>
              g.where("userGroupId", "IN", args.userGroupIds)
            )
        )
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("space", withPersonalSpaceOwner)
        .related("connectedApp", (a) =>
          a.related("roleGrantsAsScope", leanScopeGrants).one()
        )
  ),

  connectedAppGroupAccess: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("id", args.connectedAppId)
        .where(canReadConnector(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  loadSpace: defineTenantQuery(
    workflowListArgs.extend({ spaceId: z.string() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.spaces
        .where("id", args.spaceId)
        .where(canReadSpace(ctx))
        .related("workflows", (w) =>
          w
            .where("deletedAt", "IS", null)
            .whereExists("branches")
            .orderBy("createdAt", "desc")
            .limit(250)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
        )
        .one();
    }
  ),

  spaceUserStats: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceUserStats
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("user")
  ),

  spaceVolumes: defineTenantQuery(
    z.object({ spaceId: z.string(), branchId: z.string().optional() }),
    ({ args, ctx }) => {
      const base = zql.spaceVolumes
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("branch", (b) => b.related("workflow").one())
        .orderBy("name", "asc");
      return args.branchId ? base.where("branchId", args.branchId) : base;
    }
  ),

  workflowBranchRoutes: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string().optional(),
    }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          b
            .where(({ cmp }) =>
              args.branchId ? cmp("id", args.branchId) : cmp("name", "main")
            )
            .related(
              "headCommit",
              (
                c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>
              ) =>
                c
                  .related("routes")
                  .related("files", (f) =>
                    f.where("path", "LIKE", "%/config.toml").related("blob")
                  )
                  .one()
            )
            .one()
        )
        .one()
  ),

  workflowMainRoutes: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          b
            .where("name", "main")
            .related(
              "headCommit",
              (
                c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>
              ) => c.related("routes").one()
            )
            .one()
        )
        .one()
  ),

  workflowPageForRoute: defineTenantQuery(
    z.object({
      route: z.string(),
      branchId: z.string().optional(),
      workflowId: z.string().optional(),
    }),
    ({ args, ctx }) => {
      let branches = zql.workflowBranches;
      if (args.workflowId)
        branches = branches.where("workflowId", args.workflowId);
      branches = branches.where(({ or, cmp }) =>
        args.branchId
          ? args.workflowId
            ? or(cmp("id", args.branchId), cmp("name", "main"))
            : cmp("id", args.branchId)
          : or(cmp("name", "main"), cmp("userId", ctx.userId))
      );
      return branches
        .where(canReadBranch(ctx))
        .whereExists("headCommit", (c) =>
          c.whereExists("routes", (r) =>
            r.where("route", args.route).where("routeType", "webpage")
          )
        )
        .related("workflow", (w) => w.related("space"))
        .related("headCommitChat")
        .related("headCommit", (c) =>
          c
            .related("routes", (r) =>
              r.where("route", args.route).where("routeType", "webpage")
            )
            .one()
        )
        .orderBy("createdAt", "desc");
    }
  ),

  loadWorkflow: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("branches", workflowBranchSummaries)
        .one()
  ),

  workflowPages: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string().optional() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("createdBy")
        .related("contributors", recentContributors(Number.MAX_SAFE_INTEGER))
        .related("branches", workflowBranches(ctx.userId, args.branchId))
        .one()
  ),

  loadBranchMetadata: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string().optional(),
    }),
    ({ args, ctx }) => {
      const base = zql.workflowBranches
        .where("workflowId", args.workflowId)
        .where(canReadBranch(ctx))
        .related("user")
        .related("contributors", (c) => c.related("user"))
        .related("commits", (c) => c.orderBy("createdAt", "asc"))
        .related("headCommitChat")
        .related("headCommit", (c) => c.related("routes").one());
      return (args.branchId ? base.where("id", args.branchId) : base)
        .orderBy("createdAt", "asc")
        .limit(1)
        .one();
    }
  ),

  loadBranchSummary: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .where("id", args.branchId)
        .where(canReadBranch(ctx))
        .one()
  ),

  subAgentsForChat: defineTenantQuery(
    z.object({ chatId: z.string() }),
    ({ args, ctx }) =>
      zql.subAgents
        .where("parentChatId", args.chatId)
        .where("userId", ctx.userId)
        .whereExists("parentChat", (chat) =>
          chat.whereExists("personalChat", (pc) =>
            pc.where("userId", ctx.userId).where(canReadPersonalChats(ctx))
          )
        )
        .whereExists("workflow", (workflow) =>
          workflow.where(canReadWorkflow(ctx))
        )
        .related("workflow", (workflow) => workflow.related("space"))
        .orderBy("createdAt", "asc")
  ),

  personalChats: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      zql.personalChats
        .where("userId", args?.userId ?? ctx.userId)
        .where("closedAt", "IS", null)
        .where(canReadPersonalChats(ctx))
        .orderBy("createdAt", "asc")
        .related("chat", (c) => c.related("entries", fullChatEntries).one())
  ),

  personalChatSummaries: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      readablePersonalChats(ctx, args?.userId ?? ctx.userId)
        .where("closedAt", "IS", null)
        .orderBy("createdAt", "asc")
        .related("chat", (c) =>
          c.related("entries", latestUserMessageEntry).one()
        )
  ),

  // Every readable open personal chat with no relations — for per-member
  // chat counts, not for rendering chats.
  allPersonalChats: defineTenantQuery(({ ctx }) =>
    zql.personalChats
      .where("closedAt", "IS", null)
      .where(canReadPersonalChats(ctx))
  ),

  personalVolumes: defineTenantQuery(({ ctx }) =>
    zql.personalVolumes.where("userId", ctx.userId).orderBy("name", "asc")
  ),

  personalChatTabs: defineTenantQuery(({ ctx }) =>
    zql.personalChats
      .where("userId", ctx.userId)
      .where("closedAt", "IS", null)
      .whereExists("tenant", (tenant) =>
        tenant.where("personalWorkDisabledAt", "IS", null)
      )
      .related("chat")
  ),

  closedPersonalChats: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      zql.personalChats
        .where("userId", args?.userId ?? ctx.userId)
        .where("closedAt", "IS NOT", null)
        .where(canReadPersonalChats(ctx))
        .related("chat", (c) => c.related("entries"))
        .orderBy("closedAt", "desc")
  ),

  closedPersonalChatSummaries: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      readablePersonalChats(ctx, args?.userId ?? ctx.userId)
        .where("closedAt", "IS NOT", null)
        .related("chat", (c) => c.related("entries", latestUserMessageEntry))
        .orderBy("closedAt", "desc")
  ),

  loadLiveChat: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      entriesLimit: z.number().int().nonnegative().optional(),
      entriesAfter: chatHistoryCursor.optional(),
      entriesFrom: chatHistoryCursor.optional(),
    }),
    ({ args, ctx }) =>
      zql.workflowChats
        .where("workflowId", args.workflowId)
        .where("userId", ctx.userId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
        .related("chat", (c) =>
          c
            .related("entries", (e) => {
              const page = {
                limit: args.entriesLimit,
                after: args.entriesAfter,
                from: args.entriesFrom,
              };
              return isUnboundedChatHistoryPage(page)
                ? fullChatEntries(e)
                : newestChatEntries(e, page);
            })
            .one()
        )
        .one()
  ),

  loadPersonalChat: defineTenantQuery(
    z.object({ personalChatId: z.string(), userId: z.string().optional() }),
    ({ args, ctx }) =>
      readablePersonalChats(ctx, args.userId)
        .where("id", args.personalChatId)
        .related("chat", (c) => c.related("entries", fullChatEntries).one())
        .related("derivedWorkflow", (w) =>
          w
            .where(canReadWorkflow(ctx))
            .related("branches", workflowBranchSummaries)
            .one()
        )
        .related("appUsages", (u) =>
          u
            .where("type", "personal_chat")
            .related("connectedApp", (ca) =>
              ca.related("oauthConnection").related("createdBy")
            )
        )
        .one()
  ),

  loadCommitChat: defineTenantQuery(
    z.object({
      commitId: z.string(),
      entriesLimit: z.number().int().nonnegative().optional(),
      entriesAfter: chatHistoryCursor.optional(),
      entriesFrom: chatHistoryCursor.optional(),
    }),
    ({ args, ctx }) =>
      args.commitId
        ? zql.chats
            .where("commitId", args.commitId)
            .whereExists("commit", (c) =>
              c.whereExists("branch", (b) =>
                b.whereExists("workflow", (w) =>
                  w.whereExists("space", (s) => s.where(canReadSpace(ctx)))
                )
              )
            )
            .orderBy("createdAt", "desc")
            .related("entries", (e) => {
              const page = {
                limit: args.entriesLimit,
                after: args.entriesAfter,
                from: args.entriesFrom,
              };
              return isUnboundedChatHistoryPage(page)
                ? fullChatEntries(e)
                : newestChatEntries(e, page);
            })
        : zql.chats.where("commitId", "").related("entries", fullChatEntries)
  ),

  loadWorkflowChangeReview: defineTenantQuery(
    z.object({ commitId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowChangeReviews
        .where("commitId", args.commitId)
        .whereExists("commit", (c) =>
          c.whereExists("branch", (b) =>
            b.whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
          )
        )
        .orderBy("createdAt", "desc")
        .orderBy("id", "desc")
        .one()
  ),

  loadBranchChats: defineTenantQuery(
    z.object({
      branchId: z.string(),
      entriesLimit: z.number().int().nonnegative().optional(),
    }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowCommits
            .where("branchId", args.branchId)
            .whereExists("branch", (b) =>
              b.whereExists("workflow", (w) =>
                w.whereExists("space", (s) => s.where(canReadSpace(ctx)))
              )
            )
            .related("chats", (c) =>
              c
                .related("entries", (e) =>
                  args.entriesLimit === undefined
                    ? fullChatEntries(e)
                    : newestChatEntries(e, { limit: args.entriesLimit })
                )
                .one()
            )
            .orderBy("createdAt", "asc")
        : zql.workflowCommits
            .where("id", "")
            .related("chats", (c) =>
              c.related("entries", fullChatEntries).one()
            )
  ),

  loadWorkflowChatEntries: defineTenantQuery(
    z.object({
      chatIds: z.array(z.string()),
      branchId: z.string().optional(),
      commitId: z.string().optional(),
      limit: z.number().int().positive().optional(),
      after: chatHistoryCursor.optional(),
      from: chatHistoryCursor.optional(),
    }),
    ({ args, ctx }) => {
      const readableBranch = (
        branch: Query<"workflowBranches", Schema, WorkflowBranch>
      ) =>
        branch
          .where("tenantId", ctx.tenantId)
          .whereExists("workflow", (workflow) =>
            workflow.whereExists("space", (space) =>
              space.where(canReadSpace(ctx))
            )
          );
      const entries = zql.chatEntries
        .where("chatId", "IN", args.chatIds)
        .whereExists("chat", (chat) =>
          chat.whereExists("commit", (commit) =>
            args.commitId
              ? commit
                  .where("id", args.commitId)
                  .whereExists("branch", readableBranch)
              : commit.whereExists(
                  "branch",
                  (branch) =>
                    readableBranch(branch).where("id", args.branchId ?? ""),
                  { scalar: true }
                )
          )
        );
      return isUnboundedChatHistoryPage(args)
        ? fullChatEntries(entries)
        : newestChatEntries(entries, args);
    }
  ),

  loadBranchRunsSummary: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowRuns
            .where("branchId", args.branchId)
            .where(canReadRun(ctx))
            .orderBy("createdAt", "desc")
            .limit(WORKFLOW_RUNS_LIMIT)
        : zql.workflowRuns.where("id", "")
  ),

  loadRunSummary: defineTenantQuery(
    z.object({ runId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowRuns.where("id", args.runId).where(canReadRun(ctx)).one()
  ),

  // Check access on the commit so chat updates to its branch do not recheck
  // permissions for each file.
  loadCommitFiles: defineTenantQuery(
    z.object({ commitId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowCommits
        .where("id", args.commitId)
        .whereExists("branch", (b) =>
          b.whereExists("workflow", (w) =>
            w.whereExists("space", (s) => s.where(canReadSpace(ctx)))
          )
        )
        .related("files", (f) => f.related("blob"))
        .one()
  ),

  loadStepActivity: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("stepActivity", (a) => a.where("branchId", args.branchId))
        .one()
  ),

  loadStepActivityForStep: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string(),
      stepDir: z.string(),
    }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("workflowId", args.workflowId)
        .where("branchId", args.branchId)
        .where("stepDir", args.stepDir)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
        .one()
  ),

  // Dead in current clients; retained for cached clients that still use this name.
  loadBranchStepActivity: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("branchId", args.branchId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
  ),

  userRecentWorkflows: defineTenantQuery(workflowListArgs, ({ args, ctx }) =>
    zql.workflowViews
      .where("userId", ctx.userId)
      .where(canReadWorkflowView(ctx))
      .whereExists("workflow", (w) =>
        w.where("deletedAt", "IS", null).whereExists("branches")
      )
      .related("workflow", (w) => recentWorkflowDetails(w, ctx, args))
      .orderBy("lastSeenAt", "desc")
      .limit(50)
  ),

  userBranches: defineTenantQuery(
    workflowListArgs.extend({ activitySince: z.number() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .where("userId", ctx.userId)
        .where(canReadBranch(ctx))
        .related("user")
        .related("contributors", recentContributors(args.activitySince))
        .related("headCommitChat")
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("branches")
        )
        .orderBy("lastChatEntryAt", "desc")
        .limit(50)
        .related("headConnectorUsages")
  ),

  userWorkflowViews: defineTenantQuery(({ ctx }) =>
    zql.workflowViews.where("userId", ctx.userId)
  ),

  navBranches: defineTenantQuery(({ ctx }) =>
    zql.workflowBranches
      .where("userId", ctx.userId)
      .where(canReadBranch(ctx))
      .related("workflow")
      .related("headCommitChat")
      .orderBy("lastChatEntryAt", "desc")
      .limit(50)
  ),

  navSpace: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaces
        .where("id", args.spaceId)
        .where(canReadSpace(ctx))
        .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
        .one()
  ),

  userFavoriteWorkflowIds: defineTenantQuery(({ ctx }) =>
    zql.workflowFavorites
      .where("userId", ctx.userId)
      .where(canReadWorkflowFavorite(ctx))
  ),

  userFavoriteWorkflows: defineTenantQuery(
    workflowListArgs,
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflowFavorites
        .where("userId", ctx.userId)
        .where(canReadWorkflowFavorite(ctx))
        .whereExists("workflow", (w) => w.whereExists("branches"))
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
        )
        .orderBy("createdAt", "desc")
        .limit(50);
    }
  ),

  userFavoriteRouteIds: defineTenantQuery(({ ctx }) =>
    zql.routeFavorites
      .where("userId", ctx.userId)
      .where(canReadRouteFavorite(ctx))
  ),

  userFavoriteRouteWorkflows: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.routeFavorites
        .where("userId", ctx.userId)
        .where(canReadRouteFavorite(ctx))
        .whereExists("workflow", (w) =>
          w.where("deletedAt", "IS", null).whereExists("branches")
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
        )
        .orderBy("createdAt", "desc");
    }
  ),

  // All users can read AI providers (api_key is a non-replicated secret column);
  // managing them requires tenant:ai_providers_manage, enforced in the mutations.
  aiConfigs: defineTenantQuery(() =>
    zql.aiProviders
      .orderBy("createdAt", "asc")
      .related("appUsage", (u) => u.related("connectedApp").one())
  ),

  availableAIProviders: defineTenantQuery(({ ctx }) =>
    zql.aiProviders
      .where(canUseAIProvider(ctx))
      .where("disabledAt", "IS", null)
      .orderBy("createdAt", "asc")
      .related("appUsage", (u) => u.related("connectedApp").one())
  ),

  activeAIProvider: defineTenantQuery(() =>
    zql.aiProviders.where("active", true).one()
  ),

  monitoringAiBudgets: defineTenantQuery(() => zql.aiBudgets),

  monitoringUsageBudgets: defineTenantQuery(() => zql.connectorUsageBudgets),

  monitoringUsagePrices: defineTenantQuery(() => zql.connectorUsagePrices),

  monitorAiProviders: defineTenantQuery(() =>
    zql.aiProviders
      .orderBy("createdAt", "asc")
      .related("modelPricing")
      .related("budgets")
      .related("appUsage", (u) => u.one())
  ),

  loadAiProvider: defineTenantQuery(
    z.object({ aiProviderId: z.string() }),
    ({ args }) =>
      zql.aiProviders
        .where("id", args.aiProviderId)
        .related("appUsage", (u) => u.related("connectedApp").one())
        .one()
  ),

  monitoringWorkflowValue: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .whereExists("branches")
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) => b.where("name", "main"))
  ),

  branchModelPreference: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.branchModelPreferences
        .where("branchId", args.branchId)
        .where("userId", ctx.userId)
        .one()
  ),

  ssoProviderRouteDependencies: defineTenantQuery(
    z.object({
      providerId: z.string().nullable(),
      includeInherited: z.boolean(),
    }),
    ({ args, ctx }) =>
      ssoRouteDependenciesQuery(
        ctx.tenantId,
        args.providerId,
        args.includeInherited
      ).where(canReadBranch(ctx))
  ),

  ssoProviders: defineTenantQuery(({ ctx }) =>
    zql.ssoProvider
      .where(canReadSsoProvider(ctx))
      .orderBy("createdAt", "asc")
      .orderBy("id", "asc")
  ),

  oidcProviders: defineTenantQuery(
    z.object(nameKeyset).optional(),
    ({ args, ctx }) =>
      paginateQuery(
        zql.oidcProviders
          .where(canReadOidcProvider(ctx))
          .orderBy("name", "asc"),
        args
      )
  ),

  serviceAccounts: defineTenantQuery(
    z.object(nameKeyset).optional(),
    ({ args, ctx }) =>
      paginateQuery(
        zql.serviceAccounts
          .related("federations", (f) => f.related("provider"))
          .related("apiKeys", (k) => k.where(isHumanManagedServiceAccountKey()))
          .related("roleGrantsAsPrincipal", (g) =>
            g.related("scopeAsSpace", (space) => space.where(canReadSpace(ctx)))
          )
          .where(canReadServiceAccount(ctx))
          .orderBy("name", "asc"),
        args
      )
  ),

  serviceAccountFederations: defineTenantQuery(
    z.object({
      serviceAccountId: z.string(),
      ...nameKeyset,
    }),
    ({ args, ctx }) =>
      paginateQuery(
        zql.serviceAccountFederations
          .where("serviceAccountId", args.serviceAccountId)
          .whereExists("serviceAccount", (sa) =>
            sa.where(canReadServiceAccount(ctx))
          )
          .orderBy("name", "asc"),
        args
      )
  ),

  auditLogs: defineTenantQuery(
    z.object({
      limit: z.number(),
      searchText: z.string().optional(),
      success: z.boolean().optional(),
      sources: z.array(z.string()).optional(),
      userId: z.string().optional(),
      since: z.number().optional(),
    }),
    ({ args, ctx }) => {
      let query = zql.auditLogs.where(canReadAuditLog(ctx));
      const searchText = args.searchText?.trim();
      if (searchText) {
        query = query.where(
          "operationName",
          "ILIKE",
          `%${escapeLikePattern(searchText)}%`
        );
      }
      if (args.success !== undefined) {
        query = query.where("success", args.success);
      }
      if (args.sources && args.sources.length > 0) {
        query = query.where(
          "source",
          "IN",
          args.sources as AuditLog["source"][]
        );
      }
      if (args.userId !== undefined) {
        query =
          args.userId === "none"
            ? query.where("userId", "IS", null)
            : query.where("userId", args.userId);
      }
      if (args.since !== undefined) {
        query = query.where("timestamp", ">", args.since);
      }
      return query.orderBy("timestamp", "desc").limit(args.limit);
    }
  ),

  auditLogDestinations: defineTenantQuery(({ ctx }) =>
    zql.auditLogDestinations
      .orderBy("name", "asc")
      .related("appUsage", (u) => u.related("connectedApp").one())
      .where(canReadAuditLogDestination(ctx))
  ),

  loadSpaceGitRemote: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceGitRemotes
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("appUsage", (u) => u.related("connectedApp").one())
        .one()
  ),

  // Every PR (open, draft, merged, or closed) on the git branch this workflow
  // branch maps to, reached through its space branch, most-recently-updated
  // first so the UI can render a state badge linking the PR.
  pullRequestsForBranch: defineTenantQuery(
    z.object({ workflowBranchId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceGitPullRequests
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .whereExists("spaceBranch", (b) =>
          b.whereExists("members", (m) =>
            m.where("workflowBranchId", args.workflowBranchId)
          )
        )
        .orderBy("updatedAt", "desc")
  ),

  // The git branch this workflow branch maps to; absent until a sync has
  // reconciled the branch onto the remote.
  gitBranchForWorkflowBranch: defineTenantQuery(
    z.object({ workflowBranchId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceBranches
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .whereExists("members", (m) =>
          m.where("workflowBranchId", args.workflowBranchId)
        )
        .related("members", (m) =>
          m.where("workflowBranchId", args.workflowBranchId).one()
        )
        .one()
  ),

  featureFlags: defineTenantQuery(() =>
    zql.featureFlags
      .where("enabledAt", "IS NOT", null)
      .orderBy("createdAt", "desc")
      .related("createdBy")
  ),

  allFeatureFlags: defineTenantQuery(() =>
    zql.featureFlags.orderBy("createdAt", "desc").related("createdBy")
  ),

  networks: defineTenantQuery(({ ctx }) =>
    zql.networks
      .orderBy("createdAt", "desc")
      .related("createdBy")
      .related("egressRoutes")
      .where(canReadNetwork(ctx))
  ),

  egressRules: defineTenantQuery(({ ctx }) =>
    zql.egressRules
      .where("disabledAt", "IS", null)
      .orderBy("createdAt", "desc")
      .where(canReadEgressRule(ctx))
  ),

  loadNetwork: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("createdBy")
        .related("roleGrantsAsScope", leanScopeGrants)
        .one()
  ),

  networkGroupAccess: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  apiKeys: defineTenantQuery(({ ctx }) =>
    zql.apikey
      .orderBy("createdAt", "desc")
      .related("space", withPersonalSpaceOwner)
      .related("user")
      .where(canReadApiKey(ctx))
      // Service account credentials (service-account keys and UI-issued
      // sessions) live on the Service accounts page; excluding them keeps this
      // list — and the settings page's visibility — scoped to the legacy
      // user/space keys being deprecated, so minting a service account
      // credential can't resurrect the page.
      .where("serviceAccountId", "IS", null)
      // Hide everything that isn't a legacy user/space key this page is for: the
      // short-lived creds auth flows mint (`device:` / `federated:` /
      // `usersession:`) and the member-modal user keys (`userkey:`,
      // createUserApiKey), so creating one of those can't resurrect the page.
      // Keep in sync if those names change.
      .where(({ and, or, cmp }) =>
        and(
          or(cmp("name", "IS", null), cmp("name", "NOT LIKE", "device:%")),
          or(
            cmp("name", "IS", null),
            cmp("name", "NOT LIKE", `${FEDERATED_API_KEY_NAME_PREFIX}%`)
          ),
          or(cmp("name", "IS", null), cmp("name", "NOT LIKE", "usersession:%")),
          or(
            cmp("name", "IS", null),
            cmp("name", "NOT LIKE", `${USER_API_KEY_NAME_PREFIX}%`)
          )
        )
      )
  ),

  // A member's member-modal API keys (name-prefixed `userkey:` by
  // createUserApiKey), for the member modal's key list. `canReadApiKey` gates it
  // to the member themselves or a tenant user-manager, so an admin can review
  // (and revoke) another member's keys.
  userApiKeys: defineTenantQuery(
    z.object({ userId: z.string() }),
    ({ ctx, args: { userId } }) =>
      zql.apikey
        .where("referenceId", userId)
        .where(canReadApiKey(ctx))
        .where("serviceAccountId", "IS", null)
        .where("name", "LIKE", `${USER_API_KEY_NAME_PREFIX}%`)
        .orderBy("createdAt", "desc")
  ),

  deletedWorkflows: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ ctx, args: { spaceId } }) =>
      zql.workflows
        .where("spaceId", spaceId)
        .where(canReadDeletedWorkflow(ctx))
        .related("branches")
        .orderBy("deletedAt", "desc")
        .limit(200)
  ),

  allUserGroups: defineTenantQuery(() =>
    zql.userGroups
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .related("roleGrantsAsPrincipal")
      .orderBy("name", "asc")
  ),

  allUserGroupsLite: defineTenantQuery(() =>
    zql.userGroups.orderBy("name", "asc")
  ),

  allUserGroupMemberships: defineTenantQuery(
    z
      .object({
        id: z.string().optional(),
        ...nameKeyset,
      })
      .optional(),
    ({ args }) => {
      let query = zql.userGroups
        .related("roleGrantsAsScope")
        .orderBy("name", "asc");
      if (args?.id) query = query.where("id", args.id);
      return paginateQuery(query, args);
    }
  ),

  currentUserGroups: defineTenantQuery(({ ctx }) =>
    zql.userGroups
      .whereExists("roleGrantsAsScope", (mg) => mg.where("userId", ctx.userId))
      .orderBy("name", "asc")
  ),

  allRolesLite: defineTenantQuery(z.object(nameKeyset).optional(), ({ args }) =>
    paginateQuery(
      zql.roles.related("rolePermissions").orderBy("name", "asc"),
      args
    )
  ),

  selectableUserGroups: defineTenantQuery(() =>
    zql.userGroups
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .orderBy("name", "asc")
  ),

  tenantAdminGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where("builtinRoleId", "IN", [...ALLOW_ALL_ROLE_IDS])
      .where("scopeId", ctx.tenantId)
      .where("userId", "IS NOT", null)
      .related("user")
  ),

  roleGrantsByScope: defineTenantQuery(
    z.object({ scopeId: z.string() }),
    ({ args }) => leanScopeGrants(zql.roleGrants.where("scopeId", args.scopeId))
  ),

  currentUserDirectGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where("userId", ctx.userId)
      .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  currentUserGroupGrants: defineTenantQuery(({ ctx }) =>
    zql.userGroups
      .whereExists("roleGrantsAsScope", (mg) => mg.where("userId", ctx.userId))
      .related("roleGrantsAsPrincipal", (g) =>
        g.related("customRole", (cr) => cr.related("rolePermissions"))
      )
  ),

  roleGrantsByUser: defineTenantQuery(
    z.object({ userId: z.string() }),
    ({ args }) =>
      zql.roleGrants.where("userId", args.userId).related("customRole")
  ),

  roleGrantsByPrincipalGroup: defineTenantQuery(
    z.object({ userGroupId: z.string() }),
    ({ args }) => zql.roleGrants.where("userGroupId", args.userGroupId)
  ),

  roleGrantsByPrincipalServiceAccount: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.roleGrants
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  serviceAccountKeys: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.apikey
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .where(isHumanManagedServiceAccountKey())
        .orderBy("createdAt", "desc")
  ),

  // The caller's own active user-acting OAuth grants (null service account):
  // third parties they connected that act as them, shown in profile so the user
  // can revoke access.
  myOAuthGrants: defineTenantQuery(({ ctx }) =>
    zql.oauthGrant
      .where("userId", ctx.userId)
      .where("serviceAccountId", "IS", null)
      .where("revokedAt", "IS", null)
      .related("client")
      .orderBy("createdAt", "desc")
  ),

  allDirectUserGrants: defineTenantQuery(() =>
    zql.roleGrants.where("userId", "IS NOT", null)
  ),

  allDirectServiceAccountGrants: defineTenantQuery(() =>
    zql.roleGrants
      .where("serviceAccountId", "IS NOT", null)
      .related("serviceAccount")
  ),
});
